import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Calendar, User, Building2, ArrowRight, Sparkles, Search, Upload, Link, Loader2, FileText, Beaker, Hash, AlignLeft, Clock, DollarSign } from 'lucide-react';
const stepLabels = ['Opportunity', 'Basics', 'Team', 'Review'];
export function NewProposalPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Step 1: Opportunity lookup state
  const [opportunityUrl, setOpportunityUrl] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupComplete, setLookupComplete] = useState(false);
  const [opportunityData, setOpportunityData] = useState({
    name: '',
    funder: '',
    mechanism: '',
    number: '',
    deadline: '',
    description: '',
    projectLength: '',
    maxBudget: ''
  });
  // Step 2: Basics
  const [proposalTitle, setProposalTitle] = useState('');
  const handleLookup = () => {
    setIsLookingUp(true);
    setTimeout(() => {
      setOpportunityData({
        name: 'Basic Behavioral Research on Multisensory Processing (R21)',
        funder: 'NIH',
        mechanism: 'R21',
        number: 'RFA-EY-13-001',
        deadline: '2026-06-01',
        description: 'Encourages research grant applications investigating multisensory processing in perception or other behavioral and social outcomes; intended to support basic behavioral research focused on two or more sensory modalities.',
        projectLength: '2 years',
        maxBudget: '$275,000'
      });
      setIsLookingUp(false);
      setLookupComplete(true);
    }, 2500);
  };
  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);
  const handleCreate = () => {
    localStorage.setItem('newProposalTitle', proposalTitle);
    localStorage.setItem('newProposalMechanism', opportunityData.mechanism);
    localStorage.setItem('newProposalFunder', opportunityData.funder);
    localStorage.setItem('newProposalFoaNumber', opportunityData.number);
    localStorage.setItem('newProposalDeadline', opportunityData.deadline);
    navigate('/proposals/10/timeline');
  };
  const canProceedStep1 = lookupComplete;
  const canProceedStep2 = proposalTitle.trim().length > 0;
  return <div className="min-h-full bg-muted/40 p-6 sm:p-10 flex flex-col items-center" data-id="element-1009">
      <div className="w-full max-w-2xl space-y-8" data-id="element-1010">
        {/* Header */}
        <div data-id="element-1011">
          <h1 className="text-2xl font-bold text-foreground tracking-tight" data-id="element-1012">
            Create New Proposal
          </h1>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-3" data-id="element-1013">
          {stepLabels.map((label, i) => {
          const s = i + 1;
          return <div key={s} className="flex items-center gap-2" data-id="element-1014">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step >= s ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-1015">
                  {step > s ? <Check className="h-4 w-4" data-id="element-1016" /> : s}
                </div>
                <span className={`text-sm font-medium hidden sm:inline ${step >= s ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-1017">
                  {label}
                </span>
                {s < stepLabels.length && <div className="w-8 h-px bg-border mx-1" data-id="element-1018" />}
              </div>;
        })}
        </div>

        {/* Form Card */}
        <Card className="border-0 shadow-sm" data-id="element-1019">
          <CardContent className="p-8" data-id="element-1020">
            {/* Step 1: Opportunity */}
            {step === 1 && <div className="space-y-6" data-id="element-1021">
                <div data-id="element-1022">
                  <h2 className="text-base font-semibold text-foreground mb-1" data-id="element-1023">
                    Find your funding opportunity
                  </h2>
                  <p className="text-sm text-muted-foreground" data-id="element-1024">
                    Paste a link to the opportunity page or upload the
                    solicitation document(s). We'll extract the key details
                    automatically.
                  </p>
                </div>

                {/* URL Input */}
                <div className="space-y-2" data-id="element-1025">
                  <label className="text-sm font-medium text-foreground" data-id="element-1026">
                    Opportunity URL
                  </label>
                  <div className="relative" data-id="element-1027">
                    <Link className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" data-id="element-1028" />
                    <input type="url" value={opportunityUrl} onChange={e => setOpportunityUrl(e.target.value)} placeholder="https://grants.nih.gov/grants/guide/rfa-files/RFA-EY-13-001.html" className="w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-2 text-sm shadow-sm placeholder:text-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" disabled={isLookingUp} data-id="element-1029" />
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2" data-id="element-1030">
                  <label className="text-sm font-medium text-foreground" data-id="element-1031">
                    Or upload solicitation document(s)
                  </label>
                  <div className="flex items-center justify-center w-full h-20 rounded-md border border-dashed border-input bg-slate-50/50 hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-1032">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-1033">
                      <Upload className="h-4 w-4" data-id="element-1034" />
                      <span data-id="element-1035">Drop PDF here or click to browse</span>
                    </div>
                  </div>
                </div>

                {/* Look Up Button */}
                {!lookupComplete && <Button onClick={handleLookup} disabled={isLookingUp || !opportunityUrl.trim()} className="bg-indigo-600 hover:bg-indigo-700 gap-2 w-full" data-id="element-1036">
                    {isLookingUp ? <>
                        <Loader2 className="h-4 w-4 animate-spin" data-id="element-1037" />
                        Analyzing opportunity...
                      </> : <>
                        <Search className="h-4 w-4" data-id="element-1038" />
                        Look Up
                        <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[9px] px-1.5 py-0 h-4 gap-0.5 shadow-none ml-1 font-semibold rounded-full" data-id="element-1039">
                          <Sparkles className="h-2.5 w-2.5" data-id="element-1040" />
                          AI
                        </Badge>
                      </>}
                  </Button>}

                {/* Loading State */}
                {isLookingUp && <div className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-6" data-id="element-1041">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1042">
                      <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-1043">
                        <Sparkles className="h-4 w-4 text-indigo-600 animate-pulse" data-id="element-1044" />
                      </div>
                      <div data-id="element-1045">
                        <p className="text-sm font-medium text-foreground" data-id="element-1046">
                          Analyzing opportunity...
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-1047">
                          Reading and extracting key details
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3" data-id="element-1048">
                      {['Reading document...', 'Identifying funder & mechanism...', 'Extracting requirements...'].map((text, i) => <div key={i} className="flex items-center gap-2" data-id="element-1049">
                          <div className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" style={{
                    animationDelay: `${i * 0.3}s`
                  }} data-id="element-1050" />
                          <span className="text-xs text-muted-foreground" data-id="element-1051">
                            {text}
                          </span>
                        </div>)}
                    </div>
                  </div>}

                {/* Extracted Results (editable) */}
                {lookupComplete && <div className="space-y-5" data-id="element-1052">
                    <div className="flex items-center gap-2 pb-2 border-b border-border" data-id="element-1053">
                      <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-1054">
                        <Check className="h-3.5 w-3.5 text-emerald-600" data-id="element-1055" />
                      </div>
                      <span className="text-sm font-medium text-foreground" data-id="element-1056">
                        Opportunity found - please confirm details
                      </span>
                      <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium" data-id="element-1057">
                        <Sparkles className="h-3 w-3" data-id="element-1058" />
                        AI
                      </Badge>
                    </div>

                    <div className="space-y-4" data-id="element-1059">
                      <div className="space-y-2" data-id="element-1060">
                        <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1061">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1062" />
                          Opportunity Name
                        </label>
                        <input type="text" value={opportunityData.name} onChange={e => setOpportunityData({
                    ...opportunityData,
                    name: e.target.value
                  })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1063" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-1064">
                        <div className="space-y-2" data-id="element-1065">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1066">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1067" />
                            Funder
                          </label>
                          <input type="text" value={opportunityData.funder} onChange={e => setOpportunityData({
                      ...opportunityData,
                      funder: e.target.value
                    })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1068" />
                        </div>
                        <div className="space-y-2" data-id="element-1069">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1070">
                            <Beaker className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1071" />
                            Mechanism
                          </label>
                          <input type="text" value={opportunityData.mechanism} onChange={e => setOpportunityData({
                      ...opportunityData,
                      mechanism: e.target.value
                    })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1072" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-1073">
                        <div className="space-y-2" data-id="element-1074">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1075">
                            <Hash className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1076" />
                            FOA Number
                          </label>
                          <input type="text" value={opportunityData.number} onChange={e => setOpportunityData({
                      ...opportunityData,
                      number: e.target.value
                    })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1077" />
                        </div>
                        <div className="space-y-2" data-id="element-1078">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1079">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1080" />
                            Target Submission Date
                          </label>
                          <input type="date" value={opportunityData.deadline} onChange={e => setOpportunityData({
                      ...opportunityData,
                      deadline: e.target.value
                    })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1081" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-1082">
                        <div className="space-y-2" data-id="element-1083">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1084">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1085" />
                            Project Length
                          </label>
                          <input type="text" value={opportunityData.projectLength} onChange={e => setOpportunityData({
                      ...opportunityData,
                      projectLength: e.target.value
                    })} placeholder="e.g. 2 years" className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1086" />
                        </div>
                        <div className="space-y-2" data-id="element-1087">
                          <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1088">
                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1089" />
                            Max Budget
                          </label>
                          <input type="text" value={opportunityData.maxBudget} onChange={e => setOpportunityData({
                      ...opportunityData,
                      maxBudget: e.target.value
                    })} placeholder="e.g. $275,000" className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1090" />
                        </div>
                      </div>

                      <div className="space-y-2" data-id="element-1091">
                        <label className="text-sm font-medium text-foreground flex items-center gap-1.5" data-id="element-1092">
                          <AlignLeft className="h-3.5 w-3.5 text-muted-foreground" data-id="element-1093" />
                          Description
                        </label>
                        <textarea value={opportunityData.description} onChange={e => setOpportunityData({
                    ...opportunityData,
                    description: e.target.value
                  })} rows={3} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" data-id="element-1094" />
                      </div>
                    </div>
                  </div>}
              </div>}

            {/* Step 2: Basics */}
            {step === 2 && <div className="space-y-6" data-id="element-1095">
                <div data-id="element-1096">
                  <h2 className="text-base font-semibold text-foreground mb-1" data-id="element-1097">
                    Name your proposal
                  </h2>
                  <p className="text-sm text-muted-foreground" data-id="element-1098">
                    Give your proposal a working title. You can change this
                    later.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-1099">
                  <label className="text-sm font-medium text-foreground" data-id="element-1100">
                    Proposal Title
                  </label>
                  <input type="text" value={proposalTitle} onChange={e => setProposalTitle(e.target.value)} placeholder="e.g. Crossmodal Plasticity in Audio-Visual Speech Perception" className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-1101" />
                </div>

                {/* Show opportunity context */}
                {lookupComplete && <div className="rounded-lg border bg-slate-50/50 p-4 space-y-2" data-id="element-1102">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1103">
                      Linked Opportunity
                    </p>
                    <p className="text-sm font-medium text-foreground" data-id="element-1104">
                      {opportunityData.name}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground" data-id="element-1105">
                      <span data-id="element-1106">{opportunityData.funder}</span>
                      <span data-id="element-1107">·</span>
                      <span data-id="element-1108">{opportunityData.mechanism}</span>
                      <span data-id="element-1109">·</span>
                      <span data-id="element-1110">{opportunityData.number}</span>
                    </div>
                  </div>}
              </div>}

            {/* Step 3: Team */}
            {step === 3 && <div className="space-y-8" data-id="element-1111">
                <div className="space-y-4" data-id="element-1112">
                  <h3 className="text-base font-semibold text-foreground" data-id="element-1113">
                    Principal investigator
                  </h3>
                  <div className="flex items-center gap-4 p-4 rounded-lg border bg-slate-50/50" data-id="element-1114">
                    <div className="h-10 w-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold" data-id="element-1115">
                      SC
                    </div>
                    <div data-id="element-1116">
                      <p className="font-medium text-sm" data-id="element-1117">Dr. Sarah Chen</p>
                      <p className="text-xs text-muted-foreground" data-id="element-1118">
                        sarah.chen@ucsf.edu
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-auto bg-white" data-id="element-1119">
                      PI
                    </Badge>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1120">
                  <div className="flex items-center justify-between" data-id="element-1121">
                    <h3 className="text-base font-semibold text-foreground" data-id="element-1122">
                      Invite other personnel
                      <span className="text-sm font-normal text-muted-foreground ml-2" data-id="element-1123">
                        (optional)
                      </span>
                    </h3>
                    <Button variant="outline" size="sm" className="h-7 text-xs" data-id="element-1124">
                      + Add Person
                    </Button>
                  </div>
                  <div className="space-y-3" data-id="element-1125">
                    <div className="grid grid-cols-[1fr_1fr_120px] gap-3" data-id="element-1126">
                      <input placeholder="Name" className="rounded-md border border-input px-3 py-2 text-sm placeholder:text-slate-300" data-id="element-1127" />
                      <input placeholder="Email" className="rounded-md border border-input px-3 py-2 text-sm placeholder:text-slate-300" data-id="element-1128" />
                      <select className="rounded-md border border-input px-3 py-2 text-sm" data-id="element-1129">
                        <option data-id="element-1130">Co-I</option>
                        <option data-id="element-1131">Collaborator</option>
                        <option data-id="element-1132">Consultant</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1133">
                  <h3 className="text-base font-semibold text-foreground" data-id="element-1134">
                    Administrative contact
                    <span className="text-sm font-normal text-muted-foreground ml-2" data-id="element-1135">
                      (pre-filled per institutional policy)
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1136">
                    <input defaultValue="Maria Santos" placeholder="Grants Admin Name" className="rounded-md border border-input px-3 py-2 text-sm placeholder:text-slate-300" data-id="element-1137" />
                    <input defaultValue="maria.santos@ucsf.edu" placeholder="Email" className="rounded-md border border-input px-3 py-2 text-sm placeholder:text-slate-300" data-id="element-1138" />
                  </div>
                  <p className="text-[11px] text-muted-foreground" data-id="element-1139">
                    Auto-assigned based on your department's SPO routing policy.
                    You can override if needed.
                  </p>
                </div>
              </div>}

            {/* Step 4: Review */}
            {step === 4 && <div className="space-y-6" data-id="element-1140">
                <div className="rounded-lg border bg-slate-50/50 p-6 space-y-4" data-id="element-1141">
                  <div className="flex items-start justify-between" data-id="element-1142">
                    <div data-id="element-1143">
                      <h3 className="font-semibold text-lg" data-id="element-1144">
                        {proposalTitle || 'Untitled Proposal'}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground" data-id="element-1145">
                        <Building2 className="h-4 w-4" data-id="element-1146" />
                        <span data-id="element-1147">
                          {opportunityData.funder} ({opportunityData.mechanism})
                        </span>
                        <span data-id="element-1148">•</span>
                        <span data-id="element-1149">{opportunityData.number}</span>
                      </div>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-700 border-0" data-id="element-1150">
                      Draft
                    </Badge>
                  </div>

                  {opportunityData.description && <p className="text-xs text-muted-foreground leading-relaxed border-t border-border/50 pt-3" data-id="element-1151">
                      {opportunityData.description}
                    </p>}

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50" data-id="element-1152">
                    <div data-id="element-1153">
                      <span className="text-xs text-muted-foreground block mb-1" data-id="element-1154">
                        Deadline
                      </span>
                      <div className="flex items-center gap-2 text-sm font-medium" data-id="element-1155">
                        <Calendar className="h-4 w-4 text-muted-foreground" data-id="element-1156" />
                        {opportunityData.deadline ? new Date(opportunityData.deadline + 'T00:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    }) : 'Not set'}
                      </div>
                    </div>
                    <div data-id="element-1157">
                      <span className="text-xs text-muted-foreground block mb-1" data-id="element-1158">
                        Team
                      </span>
                      <div className="flex items-center gap-2 text-sm font-medium" data-id="element-1159">
                        <User className="h-4 w-4 text-muted-foreground" data-id="element-1160" />
                        Dr. Sarah Chen
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50" data-id="element-1161">
                    <div data-id="element-1162">
                      <span className="text-xs text-muted-foreground block mb-1" data-id="element-1163">
                        Project Length
                      </span>
                      <div className="flex items-center gap-2 text-sm font-medium" data-id="element-1164">
                        <Clock className="h-4 w-4 text-muted-foreground" data-id="element-1165" />
                        {opportunityData.projectLength || 'Not set'}
                      </div>
                    </div>
                    <div data-id="element-1166">
                      <span className="text-xs text-muted-foreground block mb-1" data-id="element-1167">
                        Max Budget
                      </span>
                      <div className="flex items-center gap-2 text-sm font-medium" data-id="element-1168">
                        <DollarSign className="h-4 w-4 text-muted-foreground" data-id="element-1169" />
                        {opportunityData.maxBudget || 'Not set'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 text-blue-800 text-sm p-4 rounded-md border border-blue-100" data-id="element-1170">
                  <p className="font-medium mb-1" data-id="element-1171">Ready to create?</p>
                  <p className="opacity-90" data-id="element-1172">
                    This will initialize the proposal workspace, extract key
                    milestones, key criteria, and generate an artifact checklist
                    based on the funding opportunity. This may take a few
                    minutes.
                  </p>
                </div>
              </div>}
          </CardContent>

          {/* Footer Actions */}
          <div className="p-6 border-t bg-slate-50/30 flex items-center justify-between rounded-b-lg" data-id="element-1173">
            {step > 1 ? <Button variant="outline" onClick={handleBack} data-id="element-1174">
                Back
              </Button> : <div data-id="element-1175" />}

            {step < 4 ? <Button onClick={handleNext} disabled={step === 1 && !canProceedStep1 || step === 2 && !canProceedStep2} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" data-id="element-1176">
                Next Step
              </Button> : <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-id="element-1177">
                Create Proposal
                <ArrowRight className="h-4 w-4" data-id="element-1178" />
              </Button>}
          </div>
        </Card>
      </div>
    </div>;
}